import type { Dispatch, SetStateAction } from 'react';
import Card from '@components/UI/Card';
import Button from '@components/UI/Button';
import { DeleteButton, EditButton, EmptyState } from '@components/UI';
import { showSpinner, hideSpinner } from '@components/Layout/LoadingSpinner';
import type { BacktestSortingRule, TradingProfile } from '@einhaus/project314-types';
import type { NewBacktestSortingRuleState, SaveTradingProfileSettings } from './types';
import { formatPositionSide } from '@/utils/tradeSemantics';

interface BacktestSortingRulesSectionProps {
    backtestSortingRules: BacktestSortingRule[];
    setBacktestSortingRules: Dispatch<SetStateAction<BacktestSortingRule[]>>;
    formData: TradingProfile;
    setFormData: Dispatch<SetStateAction<TradingProfile>>;
    tradingProfileId: number;
    saveBacktestSortingRule: SaveTradingProfileSettings;
    setEditingBacktestSortingRuleId: Dispatch<SetStateAction<string | null>>;
    setNewBacktestSortingRule: Dispatch<SetStateAction<NewBacktestSortingRuleState>>;
    setShowBacktestSortingRulesModal: Dispatch<SetStateAction<boolean>>;
}

const BacktestSortingRulesSection = ({
    backtestSortingRules,
    setBacktestSortingRules,
    formData,
    setFormData,
    tradingProfileId,
    saveBacktestSortingRule,
    setEditingBacktestSortingRuleId,
    setNewBacktestSortingRule,
    setShowBacktestSortingRulesModal
}: BacktestSortingRulesSectionProps) => {
    return (
        <Card className="mb-4" headerClassName="flex justify-between items-center" title="Backtest Sorting Rules">
            <Button
                variant="outline-dark"
                onClick={() => {
                    setEditingBacktestSortingRuleId(null);

                    setNewBacktestSortingRule({
                        field: '',
                        order: 'desc',
                        tradeDirection: 'either'
                    });

                    setShowBacktestSortingRulesModal(true);
                }}
            >
                + Add
            </Button>

            {backtestSortingRules.length === 0 ? (
                <EmptyState size="sm" title="No backtest sorting rules added." />
            ) : (
                <div className="overflow-x-auto">
                    <table className="table-auto w-full text-white border-collapse">
                        <thead>
                            <tr>
                                <th className="px-4 py-2 text-left">Field</th>
                                <th className="px-4 py-2 text-left">Order</th>
                                <th className="px-4 py-2 text-left">Position side</th>
                                <th className="px-4 py-2 text-left">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {backtestSortingRules.map((rule, index) => (
                                <tr
                                    className={`border-b border-zinc-700 hover:bg-zinc-700 ${
                                        index % 2 === 0 ? 'bg-zinc-800' : 'bg-zinc-750'
                                    }`}
                                    key={rule.id}
                                >
                                    <td className="px-4 py-2">{rule.field}</td>
                                    <td className="px-4 py-2">{rule.order}</td>
                                    <td className="px-4 py-2">{formatPositionSide(rule.tradeDirection)}</td>
                                    <td className="px-4 py-2">
                                        <div className="flex items-center gap-2">
                                            <EditButton
                                                size="sm"
                                                onClick={() => {
                                                    setEditingBacktestSortingRuleId(rule.id);

                                                    setNewBacktestSortingRule({
                                                        field: rule.field,
                                                        order: rule.order,
                                                        tradeDirection: rule.tradeDirection
                                                    });

                                                    setShowBacktestSortingRulesModal(true);
                                                }}
                                            />
                                            <DeleteButton
                                                size="sm"
                                                onClick={() => {
                                                    const updatedRules = backtestSortingRules.filter((r) => r.id !== rule.id);
                                                    setBacktestSortingRules(updatedRules);

                                                    // Update the form data with the backtest sorting rule removed
                                                    const updatedFormData = {
                                                        ...formData,
                                                        backtestSortingRules: updatedRules
                                                    };

                                                    setFormData(updatedFormData);
                                                    showSpinner();

                                                    // Use the backtest sorting rule mutation to save the updated settings
                                                    saveBacktestSortingRule(
                                                        {
                                                            tradingProfileId,
                                                            settings: updatedFormData
                                                        },
                                                        {
                                                            onSuccess: () => {
                                                                hideSpinner();
                                                            },
                                                            onError: () => {
                                                                hideSpinner();
                                                            }
                                                        }
                                                    );
                                                }}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Card>
    );
};

export default BacktestSortingRulesSection;
